import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { CONTACT_EMAIL, APP_URL, PORTAL_URL } from "../../lib/siteData";

const NAV = [
  ["How we help", "#pillars"],
  ["Volunteer app", "#volunteer-app"],
  ["Our approach", "#approach"],
  ["Outcomes", "#outcomes"],
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-[9998] transition-colors duration-500 ${
        scrolled || open ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="group flex items-center gap-2">
          <span className="font-heading text-2xl font-bold tracking-tight leading-none">
            <span className="text-brand">Sport</span><span className="text-wine-light">Mate</span><span className="text-brand">.ai</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9 font-body text-sm text-neutral-400">
          {NAV.map(([label, href]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-${href.replace("#", "")}`}
              className="relative py-1 transition-colors duration-300 hover:text-white after:absolute after:left-0 after:bottom-0 after:h-px after:w-0 after:bg-brand after:transition-all after:duration-300 hover:after:w-full"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-portal-link"
            className="hidden sm:inline-flex font-body text-sm text-neutral-300 hover:text-white transition-colors duration-300"
          >
            Customer Login
          </a>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-app-button"
            className="group hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 font-body text-sm font-semibold text-ink transition-colors duration-300 hover:bg-brand hover:text-ink"
          >
            Volunteer App
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5 font-body">
            {NAV.map(([label, href]) => (
              <a
                key={href}
                href={href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${href.replace("#", "")}`}
                className="text-lg text-neutral-200"
              >
                {label}
              </a>
            ))}
            <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
              <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="text-neutral-300" data-testid="mobile-portal-link">
                Customer Login
              </a>
              <a href={APP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-ink" data-testid="mobile-app-button">
                Visit the Volunteer App <ArrowUpRight className="h-4 w-4" />
              </a>
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-neutral-300" data-testid="mobile-contact-link">
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
